import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Container,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
  Chip,
} from '@mui/material';
import { Save as SaveIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Link as RouterLink } from 'react-router-dom';
import DashboardLayout from '../../layouts/DashboardLayout';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

/** Same ad models as Locsy Spin / DigiDrop — order matches Lucky Wheel. */
const AD_MODELS = [
  {
    placementKey: 'popup',
    label: 'Popup ads',
    rateKey: 'price_popup_per_day',
    enableKey: 'enable_placement_popup',
    free: false,
    displayArea: 'Full-screen popup on citizen app open (daypart slots)',
    thumb: '1080×1920 (9:16)',
    full: '1080×1920 (9:16)',
  },
  {
    placementKey: 'home_pamphlets',
    label: 'Digital pamphlets',
    rateKey: 'price_home_per_day',
    enableKey: 'enable_placement_home',
    free: false,
    displayArea: 'Citizen home — Digital Pamphlets carousel + full pamphlet',
    thumb: '1120×700 (16:10)',
    full: '1080×1920 (9:16)',
  },
  {
    placementKey: 'my_town',
    label: 'My Town',
    rateKey: 'price_my_town_per_day',
    enableKey: 'enable_placement_my_town',
    free: false,
    displayArea: 'Town Pulse — full-screen vertical reels',
    thumb: '1080×1920 (9:16)',
    full: '1080×1920 (9:16)',
  },
  {
    placementKey: 'shop_updates',
    label: 'Status / shop updates',
    rateKey: null,
    enableKey: 'enable_placement_shop_updates',
    free: true,
    displayArea: 'Shop screen + Food Zone / Fresh Market / Health rails',
    thumb: '800×500 (16:10)',
    full: '1080×1920 (9:16)',
  },
  {
    placementKey: 'category_featured',
    label: 'Category featured',
    rateKey: 'price_category_per_day',
    enableKey: 'enable_placement_category',
    free: false,
    displayArea: 'Category browse featured (e.g. Fresh Market lists)',
    thumb: '800×500 (16:10)',
    full: '1080×1920 (9:16)',
  },
];

const defaultForm = {
  price_home_per_day: 200,
  price_popup_per_day: 400,
  price_category_per_day: 200,
  price_my_town_per_day: 200,
  min_promotion_days: 1,
  max_promotion_days: 90,
  max_active_posts_per_business: 20,
  max_video_duration_seconds: 120,
  max_upload_size_mb: 25,
  payment_timing: 'pay_after_approve',
  enable_placement_shop_updates: true,
  enable_placement_home: true,
  enable_placement_popup: true,
  enable_placement_category: true,
  enable_placement_my_town: true,
  show_live_my_town: false,
  show_demo_my_town: true,
  require_manual_approval: false,
};

const emptyDigidrop = (key) => ({
  placementKey: key,
  adCost: 0,
  poolSharePercent: 0,
  couponsPerEngage: 1,
  minCouponWorth: 0,
  enabled: true,
});

const AdvertisementSettings = () => {
  const [form, setForm] = useState(defaultForm);
  const [digidropByKey, setDigidropByKey] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const authHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [settingsRes, rulesRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/business-posts/settings`, {
          headers: authHeaders(),
        }),
        axios
          .get(`${API_BASE_URL}/business-posts/coupon-flow/rules`, {
            headers: authHeaders(),
          })
          .catch(() => ({ data: { data: [] } })),
      ]);
      if (settingsRes.data?.success && settingsRes.data.data) {
        setForm({ ...defaultForm, ...settingsRes.data.data });
      }
      const rules = Array.isArray(rulesRes.data?.data) ? rulesRes.data.data : [];
      const map = {};
      for (const m of AD_MODELS) {
        const found = rules.find((r) => r.placementKey === m.placementKey);
        map[m.placementKey] = found
          ? {
              placementKey: m.placementKey,
              adCost: Number(found.adCost) || 0,
              poolSharePercent: Number(found.poolSharePercent) || 0,
              couponsPerEngage: Number(found.couponsPerEngage) || 1,
              minCouponWorth: Number(found.minCouponWorth) || 0,
              enabled: found.enabled !== false,
            }
          : emptyDigidrop(m.placementKey);
      }
      setDigidropByKey(map);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    document.title = 'Advertisement Settings - LOCSY';
    load();
  }, [load]);

  const updateDigidrop = (placementKey, patch) => {
    setDigidropByKey((prev) => ({
      ...prev,
      [placementKey]: { ...prev[placementKey], ...patch },
    }));
  };

  const poolShareTotal = useMemo(
    () =>
      AD_MODELS.reduce((a, m) => {
        const r = digidropByKey[m.placementKey];
        if (!r || r.enabled === false) return a;
        return a + (Number(r.poolSharePercent) || 0);
      }, 0),
    [digidropByKey],
  );

  const handleSave = async () => {
    setSaving(true);
    try {
      // Keep DigiDrop adCost aligned with daily rates before sync.
      // Pool % is recalculated server-side from ₹/day (or DigiDrop weight).
      const digidropRules = AD_MODELS.map((m) => {
        const d = digidropByKey[m.placementKey] || emptyDigidrop(m.placementKey);
        const rate = m.free ? Number(d.adCost) || 0 : Number(form[m.rateKey]) || 0;
        return {
          placementKey: m.placementKey,
          adCost: rate,
          couponsPerEngage: d.couponsPerEngage,
          minCouponWorth: d.minCouponWorth,
          enabled: form[m.enableKey] !== false && d.enabled !== false,
        };
      });

      const res = await axios.put(
        `${API_BASE_URL}/business-posts/settings`,
        { ...form, digidropRules },
        { headers: authHeaders() },
      );
      if (res.data?.success) {
        toast.success('Ad settings saved · DigiDrop / Lucky Wheel rates synced');
        setForm({ ...defaultForm, ...res.data.data });
        const synced = res.data?.digidrop?.rules;
        if (Array.isArray(synced) && synced.length) {
          const map = { ...digidropByKey };
          for (const r of synced) {
            map[r.placementKey] = {
              placementKey: r.placementKey,
              adCost: Number(r.adCost) || 0,
              poolSharePercent: Number(r.poolSharePercent) || 0,
              couponsPerEngage: Number(r.couponsPerEngage) || 1,
              minCouponWorth: Number(r.minCouponWorth) || 0,
              enabled: r.enabled !== false,
            };
          }
          setDigidropByKey(map);
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const num = (key) => (
    <TextField
      fullWidth
      type="number"
      label={key.replace(/_/g, ' ')}
      value={form[key]}
      onChange={(e) => setForm((f) => ({ ...f, [key]: Number(e.target.value) }))}
    />
  );

  const bool = (key, label) => (
    <FormControlLabel
      control={
        <Switch
          checked={Boolean(form[key])}
          onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.checked }))}
        />
      }
      label={label || key}
    />
  );

  return (
    <DashboardLayout>
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ sm: 'center' }}
          spacing={1}
          mb={2}
        >
          <Box>
            <Typography variant="h5" fontWeight={800}>
              Manage Ads — Pricing & Rules
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Same ad models as Locsy Spin. Daily rates sync to DigiDrop Ad cost & coupon pool %.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button
              component={RouterLink}
              to="/content/lucky-wheel"
              size="small"
              variant="outlined"
            >
              Open Lucky Wheel
            </Button>
            <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={saving || loading}
            >
              {saving ? 'Saving…' : 'Save settings'}
            </Button>
          </Stack>
        </Stack>

        <Alert severity="info" sx={{ mb: 2 }}>
          Save here updates Mitra publish pricing and DigiDrop / Lucky Wheel Ad cost + pool share
          for coupon grants. Creative sizes below drive Mitra AI prompts (thumbnail & full image).
        </Alert>

        <Paper sx={{ p: 3, mb: 2 }}>
          {loading ? (
            <Typography>Loading…</Typography>
          ) : (
            <>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                mb={1}
                flexWrap="wrap"
                gap={1}
              >
                <Typography variant="subtitle1" fontWeight={700}>
                  Ad models · DigiDrop pool total {Math.round(poolShareTotal * 10) / 10}%
                </Typography>
                <Chip
                  size="small"
                  label="Mitra: search · upload · prompt per model"
                  variant="outlined"
                />
              </Stack>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Higher ₹/day → higher DigiDrop pool % (auto on save). Coupons / engage & min worth
                control citizen Spin & Reveal grants.
              </Typography>

              <Stack spacing={1.5}>
                {AD_MODELS.map((m) => {
                  const d = digidropByKey[m.placementKey] || emptyDigidrop(m.placementKey);
                  const rateVal = m.free ? 0 : form[m.rateKey];
                  return (
                    <Paper key={m.placementKey} variant="outlined" sx={{ p: 1.5 }}>
                      <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        spacing={1}
                        alignItems={{ md: 'center' }}
                      >
                        <Box sx={{ minWidth: 180, flex: 1.2 }}>
                          <Stack direction="row" spacing={0.75} alignItems="center">
                            <Typography fontWeight={700}>{m.label}</Typography>
                            {m.free ? (
                              <Chip size="small" color="success" label="Free publish" />
                            ) : (
                              <Chip size="small" color="warning" label="Paid" />
                            )}
                          </Stack>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {m.placementKey}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {m.displayArea}
                          </Typography>
                          <Typography variant="caption" display="block" sx={{ mt: 0.25 }}>
                            Thumb {m.thumb} · Full {m.full}
                          </Typography>
                        </Box>

                        {m.free ? (
                          <TextField
                            size="small"
                            type="number"
                            label="DigiDrop weight"
                            helperText="Pool weight (publish is free)"
                            value={d.adCost ?? 0}
                            onChange={(e) =>
                              updateDigidrop(m.placementKey, {
                                adCost: Math.max(0, Number(e.target.value) || 0),
                              })
                            }
                            sx={{ width: 130 }}
                          />
                        ) : (
                          <TextField
                            size="small"
                            type="number"
                            label="₹ / day"
                            value={rateVal ?? 0}
                            onChange={(e) =>
                              setForm((f) => ({
                                ...f,
                                [m.rateKey]: Math.max(0, Number(e.target.value) || 0),
                              }))
                            }
                            sx={{ width: 110 }}
                          />
                        )}

                        <TextField
                          size="small"
                          type="number"
                          label="Pool % (auto)"
                          helperText="From ₹/day on save"
                          value={d.poolSharePercent ?? 0}
                          InputProps={{ readOnly: true }}
                          sx={{ width: 120 }}
                        />
                        <TextField
                          size="small"
                          type="number"
                          label="Coupons / engage"
                          inputProps={{ min: 0, max: 10 }}
                          value={d.couponsPerEngage ?? 1}
                          onChange={(e) =>
                            updateDigidrop(m.placementKey, {
                              couponsPerEngage: Math.max(
                                0,
                                Math.min(10, Number(e.target.value) || 0),
                              ),
                            })
                          }
                          sx={{ width: 130 }}
                        />
                        <TextField
                          size="small"
                          type="number"
                          label="Min worth ₹"
                          value={d.minCouponWorth ?? 0}
                          onChange={(e) =>
                            updateDigidrop(m.placementKey, {
                              minCouponWorth: Math.max(0, Number(e.target.value) || 0),
                            })
                          }
                          sx={{ width: 120 }}
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={Boolean(form[m.enableKey])}
                              onChange={(e) =>
                                setForm((f) => ({
                                  ...f,
                                  [m.enableKey]: e.target.checked,
                                }))
                              }
                            />
                          }
                          label="On"
                        />
                      </Stack>
                    </Paper>
                  );
                })}
              </Stack>
            </>
          )}
        </Paper>

        <Paper sx={{ p: 3 }}>
          {loading ? null : (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle1" fontWeight={700}>
                  Limits
                </Typography>
              </Grid>
              <Grid item xs={6} sm={4}>{num('min_promotion_days')}</Grid>
              <Grid item xs={6} sm={4}>{num('max_promotion_days')}</Grid>
              <Grid item xs={6} sm={4}>{num('max_active_posts_per_business')}</Grid>
              <Grid item xs={6} sm={4}>{num('max_video_duration_seconds')}</Grid>
              <Grid item xs={6} sm={4}>{num('max_upload_size_mb')}</Grid>

              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Payment timing</InputLabel>
                  <Select
                    value={form.payment_timing}
                    label="Payment timing"
                    onChange={(e) =>
                      setForm((f) => ({ ...f, payment_timing: e.target.value }))
                    }
                  >
                    <MenuItem value="pay_after_approve">
                      Pay after eligible (auto-eligible, then payment)
                    </MenuItem>
                    <MenuItem value="pay_before_publish">Pay before publish</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                {bool('require_manual_approval', 'Require manual approval (off by default)')}
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 1 }}>
                  Town Pulse screen — Live / Demo ads
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Demo ads show an amber <strong>Demo</strong> badge; paid posts show a green{' '}
                  <strong>Live</strong> badge.
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {bool('show_live_my_town', 'Show Live ads on Town Pulse')}
                  {bool('show_demo_my_town', 'Show Demo ads on Town Pulse')}
                </Box>
              </Grid>
            </Grid>
          )}
        </Paper>
      </Container>
    </DashboardLayout>
  );
};

export default AdvertisementSettings;
